// Agents on the web (PROTOCOL.md §8): a server-readable project's key escrowed to the relay's escrow key, the agent
// device's Automerge actor, and the sealed details of each agent call in the audit log. WebCrypto only, so the page,
// the Worker and the tests share it; the Apple apps do the same with CryptoKit (P256.KeyAgreement, HKDF, AES.GCM).
import { concat, fromB64u, toB64u, toHex, utf8 } from './b64.ts';
import { hkdf, open, random, seal, sha256 } from './crypto.ts';

type Bytes = Uint8Array<ArrayBuffer>;
const subtle = crypto.subtle;

/** The escrow wrap's HKDF info and the prefix of its associated data. */
export const ESCROW_INFO = 'writing-suite/escrow/v1';
export const escrowAAD = (accountId: string, projectId: string) => `${ESCROW_INFO}:${accountId}:${projectId}`;
/** 65-byte ephemeral point ‖ nonce ‖ 32-byte key ‖ tag. */
export const ESCROW_BYTES = 65 + 12 + 32 + 16;

/** The escrow key's id: Fingerprint (§2.1) of its 65-byte public point. */
export async function escrowKid(pub: Uint8Array): Promise<string> {
  return toHex((await sha256(pub)).subarray(0, 8));
}

/** `HKDF(ECDH x, salt = ephemeralPub ‖ escrowPub, "writing-suite/escrow/v1")`. */
async function wrapKey(privateKey: CryptoKey, otherPub: Uint8Array, ephemeralPub: Uint8Array, escrowPub: Uint8Array): Promise<Bytes> {
  const other = await subtle.importKey('raw', otherPub as Bytes, { name: 'ECDH', namedCurve: 'P-256' }, false, []);
  // The standard field is `public` (Workers' type definitions call it `$public`; the runtime takes `public`).
  const algorithm = { name: 'ECDH', public: other } as unknown as Parameters<typeof subtle.deriveBits>[0];
  const shared = new Uint8Array(await subtle.deriveBits(algorithm, privateKey, 256));
  return hkdf(shared, ESCROW_INFO, concat(ephemeralPub, escrowPub));
}

/** The derived key, for the test vectors. */
export async function escrowWrapKey(ephemeral: { privateKey: CryptoKey; pub: Uint8Array }, escrowPub: Uint8Array): Promise<Bytes> {
  return wrapKey(ephemeral.privateKey, escrowPub, ephemeral.pub, escrowPub);
}

/**
 * The project key wrapped to the relay's escrow key: `ephemeralPub ‖ Seal(K, projectKey, escrowAAD)` (125 bytes).
 * `ephemeral` and `nonce` only for test vectors.
 */
export async function wrapForEscrow(
  escrowPub: Uint8Array,
  accountId: string,
  projectId: string,
  projectKey: Uint8Array,
  o: { ephemeral?: { privateKey: CryptoKey; pub: Uint8Array }; nonce?: Uint8Array } = {},
): Promise<Bytes> {
  let ephemeral = o.ephemeral;
  if (!ephemeral) {
    const pair = (await subtle.generateKey({ name: 'ECDH', namedCurve: 'P-256' }, true, ['deriveBits'])) as CryptoKeyPair;
    ephemeral = { privateKey: pair.privateKey, pub: new Uint8Array((await subtle.exportKey('raw', pair.publicKey)) as ArrayBuffer) };
  }
  const k = await wrapKey(ephemeral.privateKey, escrowPub, ephemeral.pub, escrowPub);
  return concat(ephemeral.pub, await seal(k, projectKey, escrowAAD(accountId, projectId), o.nonce ?? random(12)));
}

/** The relay's side: the project key from an escrow wrap, with the escrow private key. Throws when it doesn't open. */
export async function unwrapEscrow(escrow: EscrowKey, accountId: string, projectId: string, wrapped: Uint8Array): Promise<Bytes> {
  if (wrapped.length !== ESCROW_BYTES) throw new Error('an escrowed key is 125 bytes');
  const ephemeralPub = wrapped.subarray(0, 65);
  const k = await wrapKey(escrow.privateKey, ephemeralPub, ephemeralPub, escrow.pub);
  return open(k, wrapped.subarray(65), escrowAAD(accountId, projectId));
}

export interface EscrowKey {
  privateKey: CryptoKey;
  pub: Bytes;
  kid: string;
}

/** The escrow key pair from its PKCS#8 form (the Worker secret `ESCROW_PRIVATE_KEY` is its base64url). */
export async function importEscrowKey(pkcs8: Uint8Array): Promise<EscrowKey> {
  const extractable = await subtle.importKey('pkcs8', pkcs8 as Bytes, { name: 'ECDH', namedCurve: 'P-256' }, true, ['deriveBits']);
  const jwk = (await subtle.exportKey('jwk', extractable)) as JsonWebKey;
  const pub = concat(new Uint8Array([4]), fromB64u(jwk.x!), fromB64u(jwk.y!));
  const privateKey = await subtle.importKey('pkcs8', pkcs8 as Bytes, { name: 'ECDH', namedCurve: 'P-256' }, false, ['deriveBits']);
  return { privateKey, pub, kid: await escrowKid(pub) };
}

/** A new escrow key pair: the base64url PKCS#8 to keep as the secret, and the public point. */
export async function newEscrowKey(): Promise<{ secret: string; pub: Bytes; kid: string }> {
  const pair = (await subtle.generateKey({ name: 'ECDH', namedCurve: 'P-256' }, true, ['deriveBits'])) as CryptoKeyPair;
  const pkcs8 = new Uint8Array((await subtle.exportKey('pkcs8', pair.privateKey)) as ArrayBuffer);
  const pub = new Uint8Array((await subtle.exportKey('raw', pair.publicKey)) as ArrayBuffer);
  return { secret: toB64u(pkcs8), pub, kid: await escrowKid(pub) };
}

// The agent device (§8.6) -----------------------------------------------------------------------------------------

/** The Automerge actor (32 lowercase hex characters) the agent device of an account writes as. */
export async function agentActor(deviceId: string): Promise<string> {
  return toHex((await sha256(utf8(`writing-suite/agent-actor/v1:${deviceId}`))).subarray(0, 16));
}

// The audit log (§8.7) --------------------------------------------------------------------------------------------

export const agentCallAAD = (accountId: string, projectId: string, callId: string) => `writing-suite/agent-call/v1:${accountId}:${projectId}:${callId}`;

/** What an agent call's sealed details hold. */
export interface AgentCallDetails {
  tool: string;
  arguments: unknown;
  result: unknown;
  /** The result was cut to fit (§8.7). */
  truncated?: boolean;
}

export async function sealAgentCall(projectKey: Uint8Array, accountId: string, projectId: string, callId: string, details: AgentCallDetails, nonce?: Uint8Array): Promise<Bytes> {
  return seal(projectKey, utf8(JSON.stringify(details)), agentCallAAD(accountId, projectId, callId), nonce);
}

export async function openAgentCall(projectKey: Uint8Array, accountId: string, projectId: string, callId: string, sealed: Uint8Array): Promise<AgentCallDetails> {
  return JSON.parse(new TextDecoder().decode(await open(projectKey, sealed, agentCallAAD(accountId, projectId, callId))));
}
